import { useEffect, useRef } from 'react';
import {
  doc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

// Phase 0 of the project-container restructure: lazily mirror the legacy
// project.video / project.editedVideo fields into the new
// projects/{id}/videos/main and projects/{id}/edits/timelapse subcollection
// docs, so the container-v2 UI (later phases) can read a single shape
// without a big-bang migration.
//
// Contract:
//   - old fields (project.video, project.editedVideo) are NEVER written or
//     removed by this hook — they stay authoritative during Phase 0.
//   - mirror + marker land in a single writeBatch so a partial success can't
//     leave the mirror doc out of sync with containerV2.videoMirrorKey /
//     editMirroredPath. If either marker mismatches, we re-mirror.
//     The video key is storagePath|sizeBytes|length (not path alone) because
//     analysis writes length/sizeBytes AFTER upload — a path-only key would
//     freeze durationSec at null if the project was opened mid-analysis.
//     The edit marker keys on output path (each render gets a new path).
//   - errors are best-effort: warn to the console, never throw upward, so a
//     mirror-write blip can't break ProjectView.
//
// Doc shapes documented here since this is the only writer today:
//   projects/{id}/videos/main:
//     { storagePath, sizeBytes, durationSec, capturedAt, cameraId, source,
//       createdAt }
//   projects/{id}/edits/{editId}:
//     { name, selection, settings, output: { storagePath },
//       credits: { deducted, refundIssued }, status, source,
//       createdAt, updatedAt }
//   selection === null means "the whole source video" (legacy mirror);
//   future edits will store e.g. { days: [...], cameras: [...] }.

const LEGACY_MIRROR_SOURCE = 'legacy-mirror';
const LEGACY_EDIT_ID = 'timelapse';
const LEGACY_VIDEO_ID = 'main';

/**
 * Lazily mirror the legacy project fields into container-v2 subcollections.
 * Call once from ProjectView with the live project snapshot.
 *
 * @param {object|null} project - project doc data (must include `id`).
 */
export default function useContainerMirror(project) {
  // Ref guard tracking the specific KEYS currently in flight (not a
  // boolean): an identical key is suppressed (React strict mode + fast prop
  // churn can't stack duplicate batches), but a new snapshot arriving
  // mid-commit (e.g. the analyzer filling in length/sizeBytes) produces a
  // different key and proceeds instead of being silently dropped.
  const inFlightKeysRef = useRef({ videoMirrorKey: null, editMirroredPath: null });

  // The full snapshot lives in a ref, NOT the dep array: Firestore rebuilds
  // nested objects (settings, the createdAt Timestamp) on every emit, so
  // their identities are never stable and would re-run the effect each emit.
  // Only the primitive fields below drive re-runs; the effect reads the
  // object-valued fields from this ref at write time, which always holds the
  // latest committed snapshot.
  const projectRef = useRef(project);
  projectRef.current = project;

  const projectId = project?.id;
  const videoStoragePath = project?.video?.storagePath || null;
  const videoSizeBytes = project?.video?.sizeBytes;
  const videoLength = project?.video?.length;
  const editedPath = project?.editedVideo?.path || null;
  const editedStatus = project?.editedVideo?.status;
  const creditsDeducted = project?.editedVideo?.creditsDeducted;
  const refundIssued = project?.editedVideo?.refundIssued;
  const markerVideoMirrorKey = project?.containerV2?.videoMirrorKey;
  const markerEditMirroredPath = project?.containerV2?.editMirroredPath;

  useEffect(() => {
    if (!projectId) return;

    const inFlight = inFlightKeysRef.current;

    const videoMirrorKey = videoStoragePath
      ? `${videoStoragePath}|${videoSizeBytes ?? ''}|${videoLength ?? ''}`
      : null;
    const needsVideoMirror =
      !!videoStoragePath
      && markerVideoMirrorKey !== videoMirrorKey
      && inFlight.videoMirrorKey !== videoMirrorKey;

    const needsEditMirror =
      editedStatus === 'complete'
      && !!editedPath
      && markerEditMirroredPath !== editedPath
      && inFlight.editMirroredPath !== editedPath;

    if (!needsVideoMirror && !needsEditMirror) return;

    if (needsVideoMirror) inFlight.videoMirrorKey = videoMirrorKey;
    if (needsEditMirror) inFlight.editMirroredPath = editedPath;

    // On failure, release only the keys THIS run claimed — and only if a
    // newer run hasn't already claimed a different key — so a later snapshot
    // can retry the write. On success the keys stay claimed: the committed
    // containerV2.* markers make needs* false, so no release is needed.
    const releaseClaimedKeys = () => {
      if (needsVideoMirror && inFlight.videoMirrorKey === videoMirrorKey) {
        inFlight.videoMirrorKey = null;
      }
      if (needsEditMirror && inFlight.editMirroredPath === editedPath) {
        inFlight.editMirroredPath = null;
      }
    };
    const markerUpdate = {
      schemaVersion: 1,
      migratedAt: serverTimestamp(),
    };

    try {
      const batch = writeBatch(db);

      if (needsVideoMirror) {
        const videoDocRef = doc(
          db, 'projects', projectId, 'videos', LEGACY_VIDEO_ID,
        );
        batch.set(
          videoDocRef,
          {
            storagePath: videoStoragePath,
            sizeBytes: videoSizeBytes ?? null,
            // Legacy video.length is duration in seconds — same field the
            // analyzer writes. Keep the name distinct here so downstream
            // code can rely on the container-v2 name.
            durationSec: videoLength ?? null,
            capturedAt: projectRef.current?.createdAt ?? null,
            cameraId: null,
            source: LEGACY_MIRROR_SOURCE,
            createdAt: serverTimestamp(),
          },
          { merge: true },
        );
        markerUpdate.videoMirrorKey = videoMirrorKey;
      }

      if (needsEditMirror) {
        const editDocRef = doc(
          db, 'projects', projectId, 'edits', LEGACY_EDIT_ID,
        );
        batch.set(
          editDocRef,
          {
            name: 'Timelapse',
            selection: null,
            settings: projectRef.current?.settings ?? null,
            output: { storagePath: editedPath },
            credits: {
              deducted: creditsDeducted ?? null,
              refundIssued: refundIssued ?? false,
            },
            status: 'complete',
            source: LEGACY_MIRROR_SOURCE,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
        markerUpdate.editMirroredPath = editedPath;
      }

      // Marker update on the project doc, merged so we only touch the
      // containerV2.* keys and never disturb video/editedVideo.
      const projectDocRef = doc(db, 'projects', projectId);
      batch.set(
        projectDocRef,
        { containerV2: markerUpdate },
        { merge: true },
      );

      const commitPromise = batch.commit();
      // batch.commit() returns a Promise; guard cleanup either way.
      if (commitPromise && typeof commitPromise.then === 'function') {
        commitPromise.catch((err) => {
          // Best-effort: warn, don't throw. Old fields remain authoritative.
          console.warn(
            `useContainerMirror: batch commit failed for project ${projectId}`,
            err,
          );
          releaseClaimedKeys();
        });
      }
    } catch (err) {
      // Synchronous throw (bad ref, missing db) — same best-effort posture.
      console.warn(
        `useContainerMirror: mirror setup failed for project ${projectId}`,
        err,
      );
      releaseClaimedKeys();
    }
  }, [
    projectId,
    videoStoragePath,
    videoSizeBytes,
    videoLength,
    editedPath,
    editedStatus,
    creditsDeducted,
    refundIssued,
    markerVideoMirrorKey,
    markerEditMirroredPath,
  ]);
}
